/**
 * Event Open / Expired / Closed status helpers (Events, Sign-ups, Participant Profiles).
 * Expired is a derived UI phase: stored status is Open but end (or start) date is before today.
 */
import dayjs from "dayjs";

export const EVENT_UI_STATUS_OPTIONS = ["Open", "Expired", "Closed"];

/** @param {unknown} status */
export function normalizeEventOpenClosedLabel(status) {
  return status && String(status).trim().toLowerCase() === "closed" ? "Closed" : "Open";
}

/**
 * Open event whose end (or start) calendar day is before today — matches Events tab.
 * @param {object|null|undefined} ev
 * @param {import('dayjs').Dayjs} [today]
 */
export function isEventExpired(ev, today = dayjs().startOf("day")) {
  if (normalizeEventOpenClosedLabel(ev?.status) !== "Open") return false;
  const end = ev?.end_datetime || ev?.start_datetime;
  if (!end) return false;
  const d = dayjs(end);
  return d.isValid() && d.startOf("day").isBefore(today);
}

/**
 * UI phase for filters/badges: Open | Expired | Closed.
 * @param {object|null|undefined} ev
 * @param {import('dayjs').Dayjs} [today]
 * @returns {'Open'|'Expired'|'Closed'}
 */
export function getEventUiPhase(ev, today = dayjs().startOf("day")) {
  if (normalizeEventOpenClosedLabel(ev?.status) === "Closed") return "Closed";
  if (isEventExpired(ev, today)) return "Expired";
  return "Open";
}

/**
 * Narrow event list when Status selects one or two phases.
 * Empty set or all three ⇒ no narrowing (All).
 * @param {object[]} events
 * @param {Set<string>} statusFilterSet
 */
export function filterEventsByOpenClosedStatus(events, statusFilterSet) {
  const list = Array.isArray(events) ? events : [];
  if (!statusFilterSet || statusFilterSet.size === 0) return list;
  if (statusFilterSet.size >= EVENT_UI_STATUS_OPTIONS.length) return list;
  const today = dayjs().startOf("day");
  return list.filter((ev) => statusFilterSet.has(getEventUiPhase(ev, today)));
}

/**
 * Body values for APIs that accept event_statuses including Expired (derived phase).
 * Returns null when Status is All (empty or all three).
 * @param {Set<string>} statusFilterSet
 * @returns {string[]|null}
 */
export function eventStatusesForApi(statusFilterSet) {
  if (!statusFilterSet || statusFilterSet.size === 0) return null;
  if (statusFilterSet.size >= EVENT_UI_STATUS_OPTIONS.length) return null;
  const allowed = new Set(EVENT_UI_STATUS_OPTIONS);
  const list = EVENT_UI_STATUS_OPTIONS.filter((s) => statusFilterSet.has(s) && allowed.has(s));
  return list.length > 0 ? list : null;
}

/** Label for Status dropdown trigger. */
export function formatEventStatusFilterLabel(statusFilterSet) {
  if (!statusFilterSet || statusFilterSet.size === 0) return "All";
  if (statusFilterSet.size >= EVENT_UI_STATUS_OPTIONS.length) return "All";
  return EVENT_UI_STATUS_OPTIONS.filter((s) => statusFilterSet.has(s)).join(", ");
}
